import { useState, useEffect, useCallback } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { fmt } from "@/lib/format";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import InfoTooltip from "@/components/InfoTooltip";
import { formatContributionRate, secondaryCostRows, internalBreakdownRows } from "@/lib/b2cEconomics";

// "B2C 수익성 (추정)" — 실제로 돈을 낸 고객에게서 얼마가 남는지만 따로 본다(A-4, 2026-08-14).
//
// 왜 접혀 있고 펼 때 부르는가: 이 값은 계정별 전 기간 원장을 훑어야 나온다(백엔드가 계정
// 수만큼 Query 를 돈다 — 콜드 캐시에서 2초대). 손익 추이를 보러 온 사람 전원에게 그 비용을
// 물리면 첫 로딩이 그만큼 느려진다. 그래서 기존 overview 호출에는 segments 를 붙이지 않고,
// 이 블록을 펼친 사람에게만 ?segments=1 로 따로 받는다. 화면의 "기간별 표 접기/펴기" 와
// 같은 동작이라 새로 배울 것도 없다.
//
// 왜 pool=all 인가: pool=nova 만 쓰면 sedaily CMS 사용분이 빠져 "Internal Cost" 라는 이름과
// 실제 범위가 어긋난다(2026-08-14 실측 기준 ₩26,396 누락). B2C 유료·기여이익은 nova/all
// 어느 쪽이든 같다 — 결제는 전부 nova 풀에서 일어나기 때문이다.
export default function B2cEconomics({ range }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchSegments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.overview({
        from: range.from, to: range.to,
        // 경영 요약은 사일로 전체가 기준 — 위 주석 참고.
        pool: "all",
        segments: 1,
      });
      setData(res?.accountSegments ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "불러오지 못했습니다.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  // 펼친 뒤에만 조회한다. 기간이 바뀌면 펼쳐진 상태에서만 다시 받는다.
  useEffect(() => {
    if (open) fetchSegments();
  }, [open, fetchSegments]);

  const e = data?.b2cEconomics;

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={open ? "B2C 수익성 접기" : "B2C 수익성 펴기"}
        className="flex w-full items-center justify-between rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs font-semibold text-ink-600 hover:bg-ink-50"
      >
        <span>B2C 수익성 (추정) {open ? "접기" : "펴기"}</span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-ink-100 bg-white p-3">
          <div className="mb-3 flex items-center gap-1.5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              B2C AI 기여이익 (추정)
            </h4>
            <InfoTooltip
              intro={
                "실결제 매출에서 유료·혼합 고객에게 발생한 추정 AI 원가를 차감한 값입니다. " +
                "PG수수료, VAT, 인건비, 마케팅비 등은 포함하지 않습니다."
              }
              items={[
                { term: "추정 원가", desc: "원장 사용량 × 공시 단가. 실청구액이 아닙니다." },
                { term: "직접비 기여이익과 다름", desc: "그쪽은 AWS·OpenAI 실청구에서 출발하는 별개 지표입니다. 같은 값이 아닙니다." },
                { term: "무상·내부 원가", desc: "성격이 달라 이 계산식에서 빼지 않고 아래에 따로 표시합니다." },
                { term: "mixed 계정", desc: "결제 크레딧과 무상 크레딧이 한 잔액으로 합쳐져 있어, 개별 요청이 어느 쪽에서 빠졌는지는 구분하지 않습니다." },
                { term: "집계 범위", desc: "사내(CMS) 사용분까지 포함한 전체 기준입니다." },
              ]}
            />
          </div>

          {loading && <LoadingBlock />}

          {!loading && error && (
            <p className="py-6 text-center text-xs text-ink-400">{error}</p>
          )}

          {!loading && !error && !e && (
            <p className="py-6 text-center text-xs text-ink-400">표시할 데이터가 없습니다.</p>
          )}

          {!loading && !error && e && (
            <>
              <KpiRow>
                <KpiCard
                  label="실결제 매출" tone="good"
                  value={fmt.krw(e.netCashRevenueKrw)}
                  sublabel="환불 제외 · 토스 실결제"
                />
                <KpiCard
                  label="유료·혼합 고객 AI 원가 (추정)" tone="warn" invert
                  value={fmt.krw(e.b2cPaidMixedCostKrw)}
                  sublabel="결제 이력이 있는 고객의 사용분"
                />
                <KpiCard
                  label="B2C AI 기여이익 (추정)" tone="brand" hero
                  value={fmt.krw(e.b2cAiContributionKrw)}
                  sublabel="매출 − 유료·혼합 고객 AI 원가"
                />
                <KpiCard
                  label="기여이익률 (추정)"
                  value={formatContributionRate(e.b2cAiContributionRate)}
                  sublabel="회계상 순이익률이 아닙니다"
                />
              </KpiRow>

              <div className="mt-1">
                <h5 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                  위 계산에 포함되지 않는 비용
                </h5>
                <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {secondaryCostRows(e).map((row) => (
                    <div key={row.key} className="rounded-lg bg-ink-50/70 px-3 py-2">
                      <dt className="text-[11px] text-ink-500">{row.label}</dt>
                      <dd className="mt-0.5 text-sm font-semibold tabular-nums text-ink-800">
                        {fmt.krw(row.value)}
                      </dd>
                      <dd className="mt-0.5 text-[10px] leading-tight text-ink-400">{row.hint}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setDetailOpen((v) => !v)}
                  aria-expanded={detailOpen}
                  className="flex w-full items-center justify-between rounded-lg border border-ink-100 px-3 py-1.5 text-[11px] font-medium text-ink-500 hover:bg-ink-50"
                >
                  <span>내부 비용 상세 {detailOpen ? "접기" : "펴기"}</span>
                  {detailOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                {detailOpen && (
                  <table className="mt-2 w-full text-xs">
                    <tbody>
                      {internalBreakdownRows(e).map((row) => (
                        <tr key={row.key} className="border-t border-ink-100">
                          <td className="py-1.5 pr-2 text-ink-600">{row.label}</td>
                          <td className="py-1.5 text-right tabular-nums text-ink-800">{fmt.krw(row.value)}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-ink-200 font-semibold">
                        <td className="py-1.5 pr-2 text-ink-700">합계</td>
                        <td className="py-1.5 text-right tabular-nums text-ink-900">
                          {fmt.krw(e.internalCostKrw)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              <p className="mt-2 text-[10px] leading-relaxed text-ink-400">
                추정 원가(원장 사용량 × 공시 단가) 기준이며 실청구액이 아닙니다. 사내(CMS) 사용분을 포함한 전체 범위로 계산했습니다.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// 이 블록만 로딩 처리한다 — 화면 전체를 막으면 추이 차트를 보러 온 사람까지 기다리게 된다.
function LoadingBlock() {
  return (
    <div className="animate-pulse" aria-busy="true" aria-label="B2C 수익성 불러오는 중">
      <div className="mb-4 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[76px] rounded-lg bg-ink-100/70" />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[58px] rounded-lg bg-ink-50" />
        ))}
      </div>
    </div>
  );
}
